// ─────────────────────────────────────────────────────────────────────────────
// store-heatmap-card.component.ts
// Mapa de calor geográfico de tiendas sobre Leaflet + OpenStreetMap.
// Acepta datos reales vía @Input() config o carga datos simulados de Bogotá.
// ─────────────────────────────────────────────────────────────────────────────

import {
  Component, Input, Output, EventEmitter, OnChanges, OnDestroy, AfterViewInit,
  ElementRef, NgZone, ChangeDetectionStrategy, ChangeDetectorRef,
  SimpleChanges, HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule }  from '@angular/forms';
import { MatTabsModule } from '@angular/material/tabs';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

// ── Tipos públicos ─────────────────────────────────────────────────────────

/**
 * Entrada enriquecida del catálogo de objetos de librería (query 436).
 * Permite hacer el join id→name+attributes cuando query 431 ya no embebe
 * esa información directamente en cada objeto detectado.
 */
export interface CatalogEntry {
  name:        string;
  attributes?: Array<{ attribute_type_name: string; attribute_name: string }>;
}

/** Entrada del catálogo de objetos de librería (query 436) */
export interface ProductInfo {
  id:   number;
  name: string;
}

export interface StorePoint {
  lat:          number;
  lng:          number;
  name:         string;
  city?:        string;
  locality:     string;
  visits:       number;
  avgScore:     number;
  compliance:   number;
  emptyCount?:       number;   // vacíos detectados
  contaminantCount?: number;   // contaminantes (productos fuera de categoría)
  faceCount?:        number;   // total caras detectadas (legacy)
  facesPtbn?:        number;   // ⭐ caras de productos Postobón
  facesCcx?:         number;   // ⭐ caras de competencia (CCX = Coca-Cola, etc.)
  productIds?:       number[]; // IDs únicos de library_object_ids_array (query 435)
}

export type HeatMetric = 'visits' | 'score' | 'compliance' | 'empty' | 'contaminant' | 'face';
export type HeatView = 'performance' | 'findings';

export interface StoreHeatmapCardConfig {
  title?:          string;
  stores?:         StorePoint[];                       // si viene vacío → datos simulados
  detailsByStore?: Record<string, StoreDetailRecord[]>;
  /**
   * Catálogo completo de library_object_id → datos enriquecidos (query 436).
   * Se usa para resolver nombre y atributos cuando query 431 ya no los embebe
   * directamente en cada objeto detectado.
   */
  catalog?:        Map<number, CatalogEntry>;
}

export interface StoreDetailRecord {
  imageUrl: string;
  tag: string;
  faces: number;
  empty: number;
  contaminant: number;
  timestamp: string;
  storeName?: string;
  objectName?: string;
  score?: number;
  bbox?: {
    x_min: number;
    y_min: number;
    x_max: number;
    y_max: number;
    width: number;
    height: number;
  };
  attributes?: Array<{ attribute_name: string; attribute_type_name: string }>;
  event_uuid?: string;
}

interface DetailGalleryImage {
  key:        string;
  imageUrl:   string;
  timestamp:  string;
  imgW:       number;
  imgH:       number;
  containerW: number;
  containerH: number;
  objects:    StoreDetailRecord[];
}

// ── Declaraciones para leaflet.heat (no tiene @types) ─────────────────────
declare const L: any;

// ── Paleta de colores por localidad ────────────────────────────────────────
const LOCALITY_COLORS: Record<string, string> = {
  // Bogotá
  'Kennedy':       '#6366f1',
  'Suba':          '#22d3ee',
  'Engativá':      '#f59e0b',
  'Chapinero':     '#34d399',
  'Usaquén':       '#f472b6',
  'Bosa':          '#fb923c',
  'Ciudad Bolívar':'#a78bfa',
  'Fontibón':      '#4ade80',
  'Puente Aranda': '#facc15',
  'Rafael Uribe':  '#818cf8',
  'Antonio Nariño':'#f87171',
  'Barrios Unidos':'#c084fc',
  'Teusaquillo':   '#2dd4bf',
  'Mártires':      '#fbbf24',
  'La Candelaria': '#e879f9',
  'Santa Fe':      '#818cf8',
  // Medellín
  'El Poblado':    '#6366f1',
  'Laureles':      '#10b981',
  'Belén':         '#f43f5e',
  'Robledo':       '#8b5cf6',
  'Aranjuez':      '#06b6d4',
  'Manrique':      '#ef4444',
  'Guayabal':      '#84cc16',
  'La América':    '#d946ef',
  'Buenos Aires':  '#f97316',
  'Castilla':      '#14b8a6',
};

// ── Datos simulados — ~150 tiendas en Bogotá y Medellín ───────────────────
function generateSimulatedStores(preferredNames: string[] = []): StorePoint[] {
  const rng = (seed: number) => {
    const x = Math.sin(seed) * 10000;
    return x - Math.floor(x);
  };

  const bogotaLocalities: { name: string; baseLat: number; baseLng: number; count: number }[] = [
    { name: 'Chapinero',      baseLat: 4.651, baseLng: -74.063, count:  8 },
    { name: 'Usaquén',        baseLat: 4.700, baseLng: -74.032, count:  8 },
    { name: 'Suba',           baseLat: 4.740, baseLng: -74.083, count:  9 },
    { name: 'Kennedy',        baseLat: 4.628, baseLng: -74.163, count: 10 },
    { name: 'Engativá',       baseLat: 4.700, baseLng: -74.118, count:  8 },
    { name: 'Fontibón',       baseLat: 4.680, baseLng: -74.148, count:  7 },
    { name: 'Teusaquillo',    baseLat: 4.640, baseLng: -74.083, count:  6 },
    { name: 'Barrios Unidos', baseLat: 4.668, baseLng: -74.083, count:  6 },
    { name: 'Bosa',           baseLat: 4.615, baseLng: -74.193, count:  9 },
    { name: 'Ciudad Bolívar', baseLat: 4.545, baseLng: -74.148, count:  9 },
    { name: 'Puente Aranda',  baseLat: 4.620, baseLng: -74.102, count:  7 },
    { name: 'Mártires',       baseLat: 4.607, baseLng: -74.090, count:  5 },
    { name: 'Antonio Nariño', baseLat: 4.601, baseLng: -74.098, count:  5 },
    { name: 'La Candelaria',  baseLat: 4.596, baseLng: -74.075, count:  4 },
    { name: 'Santa Fe',       baseLat: 4.607, baseLng: -74.069, count:  5 },
  ];

  const medellinLocalities: { name: string; baseLat: number; baseLng: number; count: number }[] = [
    { name: 'El Poblado',  baseLat: 6.208, baseLng: -75.572, count:  7 },
    { name: 'Laureles',    baseLat: 6.244, baseLng: -75.596, count:  6 },
    { name: 'Belén',       baseLat: 6.226, baseLng: -75.601, count:  6 },
    { name: 'Robledo',     baseLat: 6.268, baseLng: -75.582, count:  5 },
    { name: 'Aranjuez',    baseLat: 6.272, baseLng: -75.559, count:  5 },
    { name: 'Manrique',    baseLat: 6.266, baseLng: -75.551, count:  5 },
    { name: 'Guayabal',    baseLat: 6.212, baseLng: -75.591, count:  5 },
    { name: 'La América',  baseLat: 6.240, baseLng: -75.594, count:  5 },
    { name: 'Buenos Aires',baseLat: 6.228, baseLng: -75.556, count:  6 },
    { name: 'Castilla',    baseLat: 6.274, baseLng: -75.569, count:  5 },
  ];

  const stores: StorePoint[] = [];
  let seed = 42;
  let preferredIdx = 0;

  const addLocality = (loc: typeof bogotaLocalities[0], dispersion: number, city: string) => {
    for (let i = 0; i < loc.count; i++) {
      seed++;
      const dLat = (rng(seed) - 0.5) * 2 * dispersion;
      seed++;
      const dLng = (rng(seed) - 0.5) * 2 * dispersion;
      seed++;
      const visits = Math.floor(rng(seed) * 149) + 1;  // 1-150
      seed++;
      const avgScore = 0.40 + rng(seed) * 0.60;        // 0.4-1.0
      seed++;
      const compliance = 0.30 + rng(seed) * 0.70;      // 0.3-1.0
      seed++;
      const emptyCount = Math.floor(rng(seed) * 15);   // 0-15
      seed++;
      const contaminantCount = Math.floor(rng(seed) * 10); // 0-10
      seed++;
      // Caras PTBN: predominante en cada tienda (40-120)
      const facesPtbn = Math.floor(40 + rng(seed) * 80);
      seed++;
      // Caras CCX: típicamente menor, pero algunas tiendas hostiles tienen más
      const ccxBias  = rng(seed) > 0.85 ? 1.4 : 0.7;
      seed++;
      const facesCcx = Math.floor((20 + rng(seed) * 60) * ccxBias);
      seed++;
      const faceCount = facesPtbn + facesCcx;
      const name = preferredNames[preferredIdx++] ?? `Tienda ${loc.name} ${i + 1}`;
      stores.push({
        lat:        loc.baseLat + dLat,
        lng:        loc.baseLng + dLng,
        name,
        city,
        facesPtbn,
        facesCcx,
        locality:   loc.name,
        visits,
        avgScore:   Math.min(1, avgScore),
        compliance: Math.min(1, compliance),
        emptyCount,
        contaminantCount,
        faceCount,
      });
    }
  };

  bogotaLocalities.forEach(loc => addLocality(loc, 0.08, 'Bogotá'));
  medellinLocalities.forEach(loc => addLocality(loc, 0.05, 'Medellín'));

  return stores;
}

@Component({
  selector:        'app-store-heatmap-card',
  standalone:      true,
  imports:         [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './store-heatmap-card.component.html',
  styleUrl: './store-heatmap-card.component.scss'
})
export class StoreHeatmapCardComponent implements AfterViewInit, OnChanges, OnDestroy {

  @Input() config?: StoreHeatmapCardConfig;
  @Input() loading = false;
  @Input() variant: 'default' | 'alt' = 'default';
  /** Mientras el dashboard carga el query 431 con los detalles del PDV */
  @Input() detailsLoading = false;
  /** Emite cuando el usuario selecciona un PDV — el dashboard debe cargar 431 */
  @Output() storeSelected = new EventEmitter<{ pdvName: string; pdvCode: string }>();
  /** Flag interno: true desde el click hasta que llegan los detalles. Evita
      flash de "Sin imágenes" entre el click y la propagación del @Input parent → child */
  protected internalLoading = false;
  @Input() initialHeatView: HeatView = 'performance';
  @Input() set active(val: boolean) {
    if (val && this.map) {
      this.scheduleMapRefresh();
    }
  }

  // ── estado ──────────────────────────────────────────────────────────────
  metric:           HeatMetric = 'visits';
  heatView:         HeatView = 'performance';
  viewMode:         'heat' | 'markers' = 'heat';
  heatRadius        = 30;
  selectedLocality: string | null = null;
  selectedCity:     string = 'all';   // 'all' | 'Bogotá' | 'Medellín'
  searchQuery:      string = '';
  storeNameQuery:   string = '';
  searchType:       'store' | 'product' = 'store';
  @Output() productSearchEvent = new EventEmitter<{ type: 'product' | 'store', query: string }>();
  searchSubject = new Subject<string>();
  showSearchDropdown = false;
  searchDropdownResults:        StorePoint[]   = [];
  productDropdownResults:       ProductInfo[]  = [];
  /** ID del producto seleccionado actualmente en el filtro del mapa */
  selectedProductId:            number | null  = null;
  /** Nombre del producto seleccionado — se muestra en el chip de filtro activo */
  selectedProductName:          string         = '';
  simulated         = false;
  tooltip:          {
    store: StorePoint;
    x: number;
    y: number;
    flipDown?:  boolean;  // si true, el tooltip se posiciona ABAJO del marcador (cerca al top edge)
    flipLeft?:  boolean;  // si true, se ancla por la izquierda (cerca al right edge)
    flipRight?: boolean;  // si true, se ancla por la derecha (cerca al left edge)
  } | null = null;
  selectedStore:    StorePoint | null = null;
  storeDetails:     StoreDetailRecord[] = [];
  detailImages:     DetailGalleryImage[] = [];
  selectedDetailImageKey: string | null = null;
  detailByTag:      { tag: string; faces: number; empty: number; contaminant: number }[] = [];
  inferenceScoreMin     = 0;       // 0–1, filtro de score
  hoveredBboxIdx:       number | null = null;
  expandedInferenceKeys = new Set<string>();
  /**
   * ⭐ Modelo de visibilidad tipo "capas": tags en este Set están OCULTOS.
   * Vacío = todos visibles (default). Click en chip → toggle visibilidad.
   */
  hiddenInferenceTags = new Set<string>();
  /** Productos ocultos individualmente. Key = `${tag}__${productName}` */
  hiddenInferenceProducts = new Set<string>();

  isProductHidden(tag: string, name: string): boolean {
    return this.hiddenInferenceProducts.has(this.productKey(tag, name));
  }
  isProductVisible(tag: string, name: string): boolean {
    return !this.isProductHidden(tag, name);
  }

  /**
   * True si este producto coincide con el filtro de producto activo en el mapa.
   * Se usa para resaltar el producto buscado dentro del panel de inferencias.
   */
  isHighlightedProduct(productName: string): boolean {
    if (!this.selectedProductId || !this.config?.catalog) return false;
    const entry = this.config.catalog.get(this.selectedProductId);
    if (!entry) return false;
    return productName.toLowerCase() === entry.name.toLowerCase();
  }
  toggleProductVisibility(tag: string, name: string, ev?: Event): void {
    ev?.stopPropagation();
    const k = this.productKey(tag, name);
    if (this.hiddenInferenceProducts.has(k)) {
      this.hiddenInferenceProducts.delete(k);
    } else {
      this.hiddenInferenceProducts.add(k);
    }
    this.cd.markForCheck();
  }
  private productKey(tag: string, name: string): string {
    return `${String(tag).toLowerCase()}__${String(name)}`;
  }

  /** Predicado unificado: el objeto debe pasar tanto el filtro de tag como el de producto */
  shouldRenderObject(obj: StoreDetailRecord): boolean {
    if (!this.matchesActiveTags(obj.tag)) return false;
    const name = String(obj.objectName ?? obj.tag);
    return !this.isProductHidden(obj.tag, name);
  }

  /** true si este tag está oculto */
  isTagHidden(tag: string): boolean {
    return this.hiddenInferenceTags.has(String(tag).toLowerCase());
  }
  /** Inverso — más legible en el template */
  isTagVisible(tag: string): boolean { return !this.isTagHidden(tag); }
  /** true si hay al menos un tag oculto */
  get hasHiddenTags(): boolean { return this.hiddenInferenceTags.size > 0; }

  // ─── Aliases legacy para compatibilidad con código previo ─────────────────
  /** @deprecated usar hiddenInferenceTags */
  get activeInferenceTags(): Set<string> { return this.hiddenInferenceTags; }
  /** @deprecated — devuelve el primer tag oculto, si lo hay */
  get activeInferenceTab(): string | null {
    const first = this.hiddenInferenceTags.values().next().value;
    return first ?? null;
  }
  /** @deprecated usar isTagHidden */
  isTagActive(tag: string): boolean { return this.isTagHidden(tag); }
  /** @deprecated usar hasHiddenTags */
  get isFilteringByTag(): boolean { return this.hasHiddenTags; }
  /** Devuelve true si el tag DEBE renderizarse en la imagen */
  matchesActiveTags(tag: string): boolean { return this.isTagVisible(tag); }
  bboxViewMode:         'boxes' | 'heatmap' = 'boxes';
  detailExpanded:       boolean = false;
  heatDarken:           number = 82;             // 0–100, % opacidad del filtro oscuro
  storeDetailTab:       'gallery' | 'products' | 'summary' = 'gallery';
  readonly Math = Math;                          // expuesto al template

  // ── Buscador de productos en el panel de inferencias ─────────────────────
  productSearchQuery  = '';
  isolatedProductKey: string | null = null;

  // ── Zoom & pan del visor de imagen ──────────────────────────────────────
  imgZoom        = 1;
  imgTranslateX  = 0;   // px en coordenadas de pantalla
  imgTranslateY  = 0;
  protected isPanning  = false;
  private panStartX    = 0;
  private panStartY    = 0;
  private panStartTX   = 0;
  private panStartTY   = 0;

  /** ResizeObserver del wrap de imagen — recalcula bboxes al cambiar tamaño */
  private wrapResizeObs?: ResizeObserver;
  private observedWrap?: HTMLElement;
  /** Vista del mapa antes de seleccionar — para volver al cerrar */
  private savedMapView: { center: any; zoom: number } | null = null;

  stores:           StorePoint[] = [];
  localityStats:    {
    name: string;
    value: number;
    pct: number;
    displayVal: string;
    visits: number;
    empty: number;
    contaminant: number;
    facesPtbn: number;
    facesCcx: number;
    sharePtbn: number;   // % share PTBN dentro de la localidad
  }[] = [];
  localities:       string[] = [];
  totalVisits       = 0;

  // ── Leaflet internals ───────────────────────────────────────────────────
  private map:          any = null;
  private baseTileLayer: any = null;
  private heatLayer:    any = null;
  private markerGroup:  any = null;
  private mapEl!:       ElementRef<HTMLDivElement>;
  private resizeObserver: ResizeObserver | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  private searchSubscription: any;

  constructor(private zone: NgZone, private cd: ChangeDetectorRef, private host: ElementRef) {}

  // ── Lifecycle ────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(query => {
      this.searchQuery = query;
      
      // Emit event for parent to handle product search natively if they want
      this.productSearchEvent.emit({ type: this.searchType, query });

      if (this.searchType === 'store') {
        this.showSearchDropdown = query.length > 0;
        this.productDropdownResults = [];
        this.computeStats();
        this.redraw({ fit: false });

        if (this.searchQuery && this.filteredStores.length > 0) {
          this.searchDropdownResults = this.filteredStores.slice(0, 6);
          // Solo mover el mapa si no hay PDV seleccionado — evita resetear la vista activa
          if (!this.selectedStore) {
            if (this.filteredStores.length === 1) {
              const s = this.filteredStores[0];
              this.map?.flyTo([s.lat, s.lng], 15, { animate: true, duration: 0.6 });
            } else {
              const bounds = L.latLngBounds(this.filteredStores.map(s => [s.lat, s.lng]));
              this.map?.flyToBounds(bounds.pad(0.15), { animate: true, duration: 0.6 });
            }
          }
        } else {
          this.searchDropdownResults = [];
        }
      } else {
        // Modo producto: el usuario está escribiendo → limpiar selección previa
        // para que el mapa muestre TODOS los PDVs mientras se busca.
        if (this.selectedProductId != null) {
          this.selectedProductId = null;
          this.zone.runOutsideAngular(() => this.redraw({ fit: true }));
        }
        this.searchDropdownResults = [];
        if (query.length > 0) {
          const q = query.toLowerCase();
          this.productDropdownResults = this.allProductOptions
            .filter(p => p.name.toLowerCase().includes(q))
            .slice(0, 8);
          this.showSearchDropdown = true;
        } else {
          this.productDropdownResults = [];
          this.showSearchDropdown = false;
        }
      }
      this.cd.markForCheck();
    });
  }

  ngAfterViewInit(): void {
    this.heatView = this.initialHeatView;
    this.metric = 'visits';
    this.mapEl = { nativeElement: this.host.nativeElement.querySelector('.shm-map') } as any;
    this.loadStores();
    // Small delay so the host element is fully laid out before Leaflet reads its dimensions.
    // Especially important when the component is the first thing rendered on page load.
    this.zone.runOutsideAngular(() => setTimeout(() => this.initMap(), 60));
    this.bindResizeObserver();
  }

  ngOnChanges(ch: SimpleChanges): void {
    if (ch['config'] && this.map) {
      this.loadStores();
      // Si hay un PDV seleccionado (el config probablemente se actualizó por la
      // respuesta del 431), NO refittear el mapa — solo refrescar detalles.
      this.redraw({ fit: !this.selectedStore });
      if (this.selectedStore) {
        this.refreshSelectedStoreDetails();
      }
    }
    if (ch['variant'] && this.map) {
      this.setBaseTileLayer();
      this.redraw({ fit: false });
    }
  }

  /**
   * Re-evalúa los detalles del PDV seleccionado contra el config.detailsByStore
   * actualizado (típicamente después de que el dashboard responde con el 431).
   * Si estamos en modo REAL (this.simulated=false) y no hay match → lista vacía,
   * NO se genera mock con picsum (que producía imágenes aleatorias).
   */
  private refreshSelectedStoreDetails(): void {
    const store = this.selectedStore;
    if (!store) return;
    const detailsMap = this.config?.detailsByStore ?? {};
    const fromExact = detailsMap[store.name] ?? [];
    const fromNorm  = fromExact.length ? [] : this.findDetailsByNormalizedStoreName(store.name, detailsMap);

    // En modo REAL: solo data de esta tienda (no fallback a otras)
    this.storeDetails =
      fromExact.length ? fromExact :
      fromNorm.length  ? fromNorm  :
      this.simulated   ? this.buildMockStoreDetails(store) :
                         [];

    this.detailImages           = this.buildDetailGallery(this.storeDetails);
    this.selectedDetailImageKey = this.detailImages[0]?.key ?? null;
    this.detailByTag            = this.aggregateDetailsByTag(this.storeDetails);
    // ⭐ Auto-expandir el producto del filtro activo ahora que tenemos los datos reales
    if (this.expandedInferenceKeys.size === 0) {
      this.expandedInferenceKeys = this.buildInitialExpandedKeys();
    }
    // ⭐ La data del 431 llegó (vía config update) — apagar el loading interno
    this.internalLoading        = false;
    this.cd.markForCheck();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    this.map?.remove();
    this.map = null;
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  // ── Datos ────────────────────────────────────────────────────────────────
  private loadStores(): void {
    const raw = this.config?.stores;
    // Solo usamos datos reales. Si no hay tiendas del backend, mapa vacío.
    // Los datos simulados solo se usan cuando no hay config en absoluto (modo demo explícito).
    if (raw?.length) {
      this.stores    = raw;
      this.simulated = false;
    } else if (!this.config) {
      // Sin config → modo demo, útil solo en desarrollo
      this.stores    = generateSimulatedStores();
      this.simulated = true;
    } else {
      // Config presente pero sin tiendas aún (query cargando o sin resultados)
      this.stores    = [];
      this.simulated = false;
    }
    this.computeStats();
  }

  private computeStats(): void {
    const cityS = this.cityStores;
    this.totalVisits = cityS.reduce((s, t) => s + t.visits, 0);
    this.localities  = [...new Set(cityS.map(s => s.locality))];

    // agrupar por localidad (solo ciudad seleccionada)
    const locMap: Record<string, { visits: number; score: number; compliance: number; n: number }> = {};
    for (const s of cityS) {
      if (!locMap[s.locality]) locMap[s.locality] = { visits: 0, score: 0, compliance: 0, n: 0 };
      locMap[s.locality].visits     += s.visits;
      locMap[s.locality].score      += s.avgScore;
      locMap[s.locality].compliance += s.compliance;
      locMap[s.locality].n++;
    }

    const rows = Object.entries(locMap).map(([name, v]) => {
      const facesPtbn = this.sumByLocality(name, 'facesPtbn');
      const facesCcx  = this.sumByLocality(name, 'facesCcx');
      const totalFaces= facesPtbn + facesCcx;
      return {
        name,
        visits:     v.visits,
        avgScore:   v.score / v.n,
        compliance: v.compliance / v.n,
        n:          v.n,
        empty:       this.sumByLocality(name, 'emptyCount'),
        contaminant: this.sumByLocality(name, 'contaminantCount'),
        face:        this.sumByLocality(name, 'faceCount'),
        facesPtbn,
        facesCcx,
        sharePtbn:   totalFaces ? (facesPtbn / totalFaces) * 100 : 0,
      };
    });

    // métrica activa para ranking
    const getValue = (r: typeof rows[0]) =>
      this.metric === 'visits'     ? r.visits :
      this.metric === 'score'      ? r.avgScore :
      this.metric === 'compliance' ? r.compliance :
      this.metric === 'empty'      ? r.empty :
      this.metric === 'contaminant'? r.contaminant :
      r.face;

    const display = (r: typeof rows[0]) =>
      this.metric === 'visits'     ? String(r.visits) :
      this.metric === 'score'      ? (r.avgScore * 100).toFixed(0) + '%' :
      this.metric === 'compliance' ? (r.compliance * 100).toFixed(0) + '%' :
      this.metric === 'empty'      ? String(r.empty) :
      this.metric === 'contaminant'? String(r.contaminant) :
      String(r.face);

    rows.sort((a, b) => getValue(b) - getValue(a));
    const max = getValue(rows[0]) || 1;

    this.localityStats = rows.map(r => ({
      name:        r.name,
      value:       getValue(r),
      pct:         (getValue(r) / max) * 100,
      displayVal:  display(r),
      visits:      r.visits,
      empty:       r.empty,
      contaminant: r.contaminant,
      facesPtbn:   r.facesPtbn,
      facesCcx:    r.facesCcx,
      sharePtbn:   r.sharePtbn,
    }));
  }

  // ── Mapa ─────────────────────────────────────────────────────────────────
  private initMap(): void {
    if (!this.mapEl?.nativeElement) return;

    this.map = L.map(this.mapEl.nativeElement, {
      center:       [4.651, -74.095],
      zoom:         11,
      zoomControl:  true,
      attributionControl: false,
    });
    this.setBaseTileLayer();
    this.map.on('zoomend', () => {
      this.redraw({ fit: false });
    });

    this.redraw();
  }

  private redraw(opts?: { fit?: boolean }): void {
    if (!this.map) return;
    // Defensa: al destruir los marcadores, el tooltip de hover queda huérfano
    // porque su mouseout nunca se dispara. Lo borramos aquí siempre.
    if (this.tooltip) {
      this.tooltip = null;
      this.cd.markForCheck();
    }
    this.clearLayers();

    const filtered = this.filteredStores;

    if (opts?.fit !== false) {
      this.fitMapToPoints(filtered);
    }

    if (this.viewMode === 'heat') {
      this.drawHeat(filtered);
    } else {
      this.drawMarkers(filtered);
    }
  }

  /** Ciudades únicas detectadas en las tiendas + opción "Todas" */
  get cities(): string[] {
    const set = new Set<string>();
    this.stores.forEach(s => { if (s.city) set.add(s.city); });
    return Array.from(set).sort();
  }

  /** Filtra por ciudad seleccionada (o todas si selectedCity vacío) */
  get cityStores(): StorePoint[] {
    if (!this.selectedCity || this.selectedCity === 'all') return this.stores;
    return this.stores.filter(s => s.city === this.selectedCity);
  }

  onCityChange(city: string): void {
    this.selectedCity        = city;
    this.selectedLocality    = null;
    this.searchQuery         = '';
    this.selectedProductId   = null;       // ⭐ limpiar filtro de producto al cambiar ciudad
    this.productDropdownResults = [];
    this.searchDropdownResults  = [];
    this.showSearchDropdown     = false;
    if (this.selectedStore) this.closeDetail();
    this.computeStats();
    this.redraw();
    // Encuadrar el mapa a la nueva selección
    setTimeout(() => {
      if (this.filteredStores.length) {
        const bounds = L.latLngBounds(this.filteredStores.map(s => [s.lat, s.lng]));
        this.map?.fitBounds(bounds.pad(0.15), { animate: true, duration: 0.5 });
      }
    }, 100);
  }

  onSearchTypeChange(type: 'store' | 'product'): void {
    this.searchType        = type;
    this.searchQuery       = '';
    this.selectedProductId = null;
    this.searchDropdownResults   = [];
    this.productDropdownResults  = [];
    this.showSearchDropdown      = false;
    this.computeStats();
    this.redraw();
  }

  onSearchChange(query: string): void {
    this.searchSubject.next(query);
  }

  selectStoreFromSearch(store: StorePoint): void {
    this.searchQuery = '';
    this.showSearchDropdown = false;
    this.searchDropdownResults = [];
    this.computeStats();
    this.redraw({ fit: false });
    this.map?.flyTo([store.lat, store.lng], 16, { duration: 0.8 });

    // Auto-open detail after flyTo animation
    setTimeout(() => {
      this.selectStore(store);
      this.cd.markForCheck();
    }, 800);
  }

  /** Selecciona un producto del catálogo — filtra el mapa a los PDVs que lo tienen */
  selectProductFromSearch(product: ProductInfo): void {
    this.selectedProductId      = product.id;
    this.selectedProductName    = product.name;
    this.searchQuery            = '';             // vaciar input para que el foco no reabra el dropdown
    this.showSearchDropdown     = false;
    this.productDropdownResults = [];
    this.viewMode               = 'markers';      // marcadores individuales hacen visible el filtro
    this.computeStats();
    this.zone.runOutsideAngular(() => this.redraw({ fit: true }));
    this.cd.markForCheck();
  }

  /** Cantidad de PDVs que tienen el producto seleccionado (para el badge del dropdown) */
  storeCountForProduct(productId: number): number {
    return this.cityStores.filter(s => s.productIds?.includes(productId)).length;
  }

  /**
   * Lista de productos únicos presentes en al menos una tienda de la ciudad activa,
   * ordenados por nombre. Alimenta el autocomplete del buscador de producto.
   */
  get allProductOptions(): ProductInfo[] {
    const catalog = this.config?.catalog;
    if (!catalog?.size) return [];
    const seen    = new Set<number>();
    const result: ProductInfo[] = [];
    this.cityStores.forEach(s =>
      s.productIds?.forEach(id => {
        if (!seen.has(id) && catalog.has(id)) {
          seen.add(id);
          result.push({ id, name: catalog.get(id)!.name });
        }
      })
    );
    return result.sort((a, b) => a.name.localeCompare(b.name));
  }

  clearSearch(): void {
    this.searchQuery            = '';
    this.selectedProductId      = null;
    this.selectedProductName    = '';
    this.showSearchDropdown     = false;
    this.searchDropdownResults  = [];
    this.productDropdownResults = [];
    this.viewMode               = 'heat';         // volver a mapa de calor al quitar el filtro
    this.computeStats();
    this.zone.runOutsideAngular(() => this.redraw({ fit: true }));
    this.cd.markForCheck();
  }

  get filteredCount(): number {
    return this.filteredStores.length;
  }

  get totalCount(): number {
    return this.cityStores.length;
  }

  get totalEmpty(): number {
    return this.filteredStores.reduce((acc, s) => acc + Number(s.emptyCount ?? 0), 0);
  }

  get totalContaminant(): number {
    return this.filteredStores.reduce((acc, s) => acc + Number(s.contaminantCount ?? 0), 0);
  }

  get totalFace(): number {
    return this.filteredStores.reduce((acc, s) => acc + Number(s.faceCount ?? 0), 0);
  }

  /** Pool usado para los KPIs del header (respeta filtros de ciudad + búsqueda + localidad) */
  private get kpiPool(): StorePoint[] {
    return this.filteredStores;
  }

  get metricTotalVisits(): number {
    return this.kpiPool.reduce((acc, s) => acc + s.visits, 0);
  }

  get metricTotalEmpty(): number {
    return this.kpiPool.reduce((acc, s) => acc + Number(s.emptyCount ?? 0), 0);
  }

  get metricTotalContaminant(): number {
    return this.kpiPool.reduce((acc, s) => acc + Number(s.contaminantCount ?? 0), 0);
  }

  /** ⭐ Caras de productos Postobón */
  get metricTotalPtbn(): number {
    return this.kpiPool.reduce((acc, s) => acc + Number(s.facesPtbn ?? 0), 0);
  }

  /** ⭐ Caras de competencia (CCX = Coca-Cola, etc.) */
  get metricTotalCcx(): number {
    return this.kpiPool.reduce((acc, s) => acc + Number(s.facesCcx ?? 0), 0);
  }

  /** % participación PTBN sobre total (PTBN + CCX) */
  get sharePtbnPct(): number {
    const total = this.metricTotalPtbn + this.metricTotalCcx;
    return total ? (this.metricTotalPtbn / total) * 100 : 0;
  }

  get shareCcxPct(): number {
    return 100 - this.sharePtbnPct;
  }


  get selectedDetailImage(): DetailGalleryImage | null {
    if (!this.detailImages.length) return null;
    if (!this.selectedDetailImageKey) return this.detailImages[0];
    return this.detailImages.find(i => i.key === this.selectedDetailImageKey) ?? this.detailImages[0];
  }

  get selectedImageObjectSummary(): Array<{
    key: string;
    name: string;
    tag: string;
    count: number;
    faces: number;
    empty: number;
    contaminant: number;
  }> {
    const img = this.selectedDetailImage;
    if (!img) return [];
    const map: Record<string, { name: string; tag: string; count: number; faces: number; empty: number; contaminant: number }> = {};
    img.objects.forEach((obj) => {
      const name = String(obj.objectName ?? obj.tag ?? 'Sin nombre');
      const tag = String(obj.tag ?? 'otros');
      const key = `${name}__${tag}`;
      if (!map[key]) map[key] = { name, tag, count: 0, faces: 0, empty: 0, contaminant: 0 };
      map[key].count += 1;
      map[key].faces += Number(obj.faces ?? 0);
      map[key].empty += Number(obj.empty ?? 0);
      map[key].contaminant += Number(obj.contaminant ?? 0);
    });
    return Object.entries(map)
      .map(([key, value]) => ({ key, ...value }))
      .sort((a, b) => b.count - a.count || b.faces - a.faces);
  }

  /** Dirección del PDV seleccionado (si la trae) */
  get storeAddress(): string {
    return (this.selectedStore as any)?.address ?? '';
  }

  /**
   * Contadores de detecciones por tag, calculados desde los StoreDetailRecords
   * del query 431 ya cargados. Cada record es un objeto detectado → contamos
   * directamente por tag. Se actualizan solos al llegar la respuesta del 431.
   */
  get detailFacesPtbn(): number {
    return this.storeDetails.filter(r => r.tag === 'postobon').length;
  }
  get detailFacesCcx(): number {
    return this.storeDetails.filter(r => r.tag === 'competencia').length;
  }
  get detailEmptyCount(): number {
    return this.storeDetails.filter(r => r.tag === 'vacios').length;
  }

  /** Última fecha de visita del PDV — sacada de los detailImages */
  get storeLastVisit(): string {
    if (!this.detailImages?.length) return '';
    const timestamps = this.detailImages
      .map(i => i.timestamp)
      .filter(Boolean)
      .sort();
    const last = timestamps[timestamps.length - 1];
    return last ? this.formatTimelineLabel(last) : '';
  }

  /** Total de visitas (imágenes/reconocimientos) registradas para el PDV */
  get storeTotalVisits(): number {
    return this.detailImages?.length ?? 0;
  }

  /** Total de productos detectados en la imagen seleccionada */
  get selectedImageTotalProducts(): number {
    return this.selectedDetailImage?.objects.length ?? 0;
  }

  /** Objeto actualmente bajo el cursor (para el inspector estático) */
  get hoveredObj(): StoreDetailRecord | null {
    if (this.hoveredBboxIdx == null) return null;
    return this.selectedDetailImage?.objects[this.hoveredBboxIdx] ?? null;
  }

  /** Atributos del objeto bajo cursor — formateados como label/value */
  get hoveredObjAttrs(): Array<{ label: string; value: string }> {
    const obj: any = this.hoveredObj;
    if (!obj?.attributes?.length) return [];
    // Tomamos los atributos más relevantes para mostrar
    const relevant = ['brand', 'category', 'size', 'company', 'shape'];
    const result: Array<{ label: string; value: string }> = [];
    relevant.forEach(type => {
      const found = obj.attributes.find((a: any) => a.attribute_type_name === type);
      if (found) {
        const label = ({ brand: 'Marca', category: 'Categoría', size: 'Tamaño',
                         company: 'Empresa', shape: 'Forma' } as any)[type] ?? type;
        result.push({ label, value: found.attribute_name });
      }
    });
    return result;
  }

  /** UUID del evento de la imagen activa (para link al Workspace) */
  get selectedImageEventUuid(): string | null {
    const img = this.selectedDetailImage;
    if (!img) return null;
    const obj: any = img.objects?.[0];
    return obj?.event_uuid ?? null;
  }

  openInWorkspace(eventUuid?: string | null): void {
    if (!eventUuid) return;
    const url = `/playground/visualizer/${eventUuid}`;
    window.open(url, '_blank', 'noopener');
  }

  /** Caras PTBN en la imagen (tag postobon) */
  get selectedImageFacesPtbn(): number {
    return this.selectedDetailImage?.objects.filter(o => String(o.tag).toLowerCase() === 'postobon').length ?? 0;
  }

  /** Caras CCX en la imagen (tag competencia) */
  get selectedImageFacesCcx(): number {
    return this.selectedDetailImage?.objects.filter(o => String(o.tag).toLowerCase() === 'competencia').length ?? 0;
  }

  /** % share PTBN en la imagen */
  get selectedImageSharePtbn(): number {
    const p = this.selectedImageFacesPtbn;
    const c = this.selectedImageFacesCcx;
    return (p + c) ? (p / (p + c)) * 100 : 0;
  }

  /** Categorías detectadas en la imagen (gaseosa, agua, jugo, etc.) */
  get selectedImageCategories(): Array<{ name: string; count: number; tag: string }> {
    const img = this.selectedDetailImage;
    if (!img) return [];
    const map: Record<string, { name: string; count: number; tags: Set<string> }> = {};
    img.objects.forEach((o: any) => {
      const attrs = o.attributes ?? [];
      const cat = attrs.find((a: any) => a?.attribute_type_name === 'category')?.attribute_name;
      if (!cat) return;
      if (!map[cat]) map[cat] = { name: cat, count: 0, tags: new Set() };
      map[cat].count++;
      map[cat].tags.add(String(o.tag).toLowerCase());
    });
    return Object.values(map)
      .map(c => ({ name: c.name, count: c.count, tag: c.tags.has('postobon') ? 'postobon' : 'competencia' }))
      .sort((a, b) => b.count - a.count);
  }

  /**
   * Estadísticas agregadas para el tab Resumen — basadas en TODAS las imágenes
   * del PDV seleccionado (no solo la activa).
   */
  get storeSummary(): {
    totalImages: number;
    totalDetections: number;
    dateFirst: string;
    dateLast: string;
    topBrands: Array<{ name: string; count: number }>;
    topProducts: Array<{ name: string; count: number; tag: string }>;
    topCategories: Array<{ name: string; count: number; share: number }>;
    topSizes: Array<{ size: string; count: number; share: number }>;
  } {
    const imgs = this.detailImages;
    const allObjects: any[] = imgs.flatMap(i => i.objects);

    // Brand counter
    const brands: Record<string, number> = {};
    const products: Record<string, { count: number; tag: string }> = {};
    const cats: Record<string, number> = {};
    const sizes: Record<string, number> = {};

    allObjects.forEach((o: any) => {
      const attrs: any[] = o.attributes ?? [];
      const brand = attrs.find(a => a?.attribute_type_name === 'brand')?.attribute_name;
      const cat   = attrs.find(a => a?.attribute_type_name === 'category')?.attribute_name;
      const size  = attrs.find(a => a?.attribute_type_name === 'size')?.attribute_name;
      const name  = o.objectName ?? o.tag ?? 'Sin nombre';

      if (brand) brands[brand] = (brands[brand] || 0) + 1;
      if (cat)   cats[cat]     = (cats[cat]   || 0) + 1;
      if (size)  sizes[size]   = (sizes[size] || 0) + 1;
      if (!products[name]) products[name] = { count: 0, tag: o.tag };
      products[name].count++;
    });

    // Date range
    const timestamps = imgs.map(i => i.timestamp).filter(Boolean).sort();

    const totalCatCount  = Object.values(cats).reduce((a, b) => a + b, 0) || 1;
    const totalSizeCount = Object.values(sizes).reduce((a, b) => a + b, 0) || 1;

    return {
      totalImages:     imgs.length,
      totalDetections: allObjects.length,
      dateFirst:       timestamps[0] ?? '',
      dateLast:        timestamps[timestamps.length - 1] ?? '',
      topBrands: Object.entries(brands)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count).slice(0, 8),
      topProducts: Object.entries(products)
        .map(([name, v]) => ({ name, count: v.count, tag: v.tag }))
        .sort((a, b) => b.count - a.count).slice(0, 8),
      topCategories: Object.entries(cats)
        .map(([name, count]) => ({ name, count, share: (count / totalCatCount) * 100 }))
        .sort((a, b) => b.count - a.count),
      topSizes: Object.entries(sizes)
        .map(([size, count]) => ({ size, count, share: (count / totalSizeCount) * 100 }))
        .sort((a, b) => b.count - a.count),
    };
  }

  /**
   * Distribución vertical en la nevera por banda (alto / medio / bajo).
   * Calcula el centro vertical del bbox y lo asigna a una de 3 bandas según
   * el alto natural de la imagen. Compara PTBN vs CCX por banda.
   */
  get fridgeVerticalDistribution(): Array<{
    band: string;
    label: string;
    ptbn: number;
    ccx:  number;
    total: number;
    sharePtbn: number;
    pctOfFridge: number;
  }> {
    const img = this.selectedDetailImage;
    if (!img || !img.imgH) return [];

    const bands = [
      { band: 'top', label: 'Arriba', ptbn: 0, ccx: 0, min: 0,    max: 1/3 },
      { band: 'mid', label: 'Medio',  ptbn: 0, ccx: 0, min: 1/3,  max: 2/3 },
      { band: 'bot', label: 'Abajo',  ptbn: 0, ccx: 0, min: 2/3,  max: 1   },
    ];

    img.objects.forEach((o: any) => {
      if (!o.bbox) return;
      const tag = String(o.tag).toLowerCase();
      if (tag !== 'postobon' && tag !== 'competencia') return;
      // Centro vertical normalizado (0=arriba, 1=abajo)
      const yCenter = ((o.bbox.y_min + o.bbox.y_max) / 2) / img.imgH;
      const band = bands.find(b => yCenter >= b.min && yCenter <= b.max) ?? bands[1];
      if (tag === 'postobon') band.ptbn++;
      else band.ccx++;
    });

    const grandTotal = bands.reduce((s, b) => s + b.ptbn + b.ccx, 0) || 1;
    return bands.map(b => {
      const total = b.ptbn + b.ccx;
      return {
        band:        b.band,
        label:       b.label,
        ptbn:        b.ptbn,
        ccx:         b.ccx,
        total,
        sharePtbn:   total ? (b.ptbn / total) * 100 : 0,
        pctOfFridge: (total / grandTotal) * 100,
      };
    });
  }

  /** Presentaciones (sizes) detectadas: 1500ml, 1L, etc. */
  get selectedImageSizes(): Array<{ size: string; count: number }> {
    const img = this.selectedDetailImage;
    if (!img) return [];
    const map: Record<string, number> = {};
    img.objects.forEach((o: any) => {
      const attrs = o.attributes ?? [];
      const size = attrs.find((a: any) => a?.attribute_type_name === 'size')?.attribute_name;
      if (!size) return;
      map[size] = (map[size] || 0) + 1;
    });
    return Object.entries(map)
      .map(([size, count]) => ({ size, count }))
      .sort((a, b) => b.count - a.count);
  }

  /**
   * Lista agrupada por TAG → productos → items individuales con score.
   * Filtra por inferenceScoreMin (0-1).
   */
  get inferencesByTag(): Array<{
    tag: string;
    totalCount: number;
    products: Array<{
      key: string;
      name: string;
      tag: string;
      count: number;
      items: Array<{ objIndex: number; score: number }>;
    }>;
  }> {
    const img = this.selectedDetailImage;
    if (!img) return [];

    const minScore = this.inferenceScoreMin || 0;
    // Orden de tags para display
    const TAG_ORDER = ['postobon', 'competencia', 'precios', 'vacios', 'nevera'];

    // Agrupar: tag → nombre_producto → lista de { objIndex, score }
    const tagMap: Record<string, Record<string, Array<{ objIndex: number; score: number }>>> = {};

    img.objects.forEach((obj, idx) => {
      const score = obj.score ?? 1;
      if (minScore > 0 && score < minScore) return;

      const tag  = String(obj.tag ?? 'otros').toLowerCase().trim();
      const name = String(obj.objectName ?? obj.tag ?? 'Sin nombre');

      if (!tagMap[tag]) tagMap[tag] = {};
      if (!tagMap[tag][name]) tagMap[tag][name] = [];
      tagMap[tag][name].push({ objIndex: idx, score });
    });

    // Ordenar tags según TAG_ORDER, los desconocidos al final
    const tags = Object.keys(tagMap).sort((a, b) => {
      const ia = TAG_ORDER.indexOf(a);
      const ib = TAG_ORDER.indexOf(b);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });

    return tags.map(tag => {
      const prodMap = tagMap[tag];
      const products = Object.entries(prodMap)
        .map(([name, items]) => ({
          key:   `${tag}__${name}`,
          name,
          tag,   // tag propio del producto — necesario para toggle/hide cuando hay merge
          count: items.length,
          items: items.sort((a, b) => b.score - a.score), // mayor score primero
        }))
        .sort((a, b) => b.count - a.count);

      return {
        tag,
        totalCount: products.reduce((s, p) => s + p.count, 0),
        products,
      };
    });
  }

  /** Grupo correspondiente al tab activo (o el primero si nada activo) */
  get activeInferenceGroup() {
    const groups = this.inferencesByTag;
    if (!groups.length) return null;
    // Combinar productos de todos los tags VISIBLES (los no ocultos)
    const visible = groups.filter(g => this.isTagVisible(g.tag));
    if (visible.length === 0) return null;
    if (visible.length === 1) return visible[0];
    // Más de uno visible → mostrar agregado
    const merged: typeof visible[0] = {
      tag:        visible.map(g => g.tag).join(' + '),
      totalCount: visible.reduce((s, g) => s + g.totalCount, 0),
      products:   visible.flatMap(g => g.products)
                          .sort((a, b) => b.count - a.count),
    };
    return merged;
  }

  // ── Buscador + aislamiento de productos ──────────────────────────────────

  /** Lista filtrada por el texto del buscador (se itera en el template) */
  get filteredInferenceProducts() {
    const group = this.activeInferenceGroup;
    if (!group?.products.length) return [];
    if (!this.productSearchQuery.trim()) return group.products;
    const q = this.productSearchQuery.trim().toLowerCase();
    return group.products.filter(p => p.name.toLowerCase().includes(q));
  }

  /** Nombre legible del producto aislado (para el chip) */
  get isolatedProductName(): string {
    if (!this.isolatedProductKey) return '';
    // key = "${tag}__${name}" — extraemos la parte después del primer __
    const idx = this.isolatedProductKey.indexOf('__');
    return idx >= 0 ? this.isolatedProductKey.slice(idx + 2) : this.isolatedProductKey;
  }

  /**
   * Click en una fila de producto:
   *  - Si ya está aislado → desaislar (restaurar todos)
   *  - Si otro producto está aislado → aislar el nuevo
   *  - Si ninguno aislado → aislar este (y colapsar/expandir scores como antes)
   */
  onProductRowClick(tag: string, name: string, key: string): void {
    if (this.isolatedProductKey === key) {
      this.clearProductIsolation();
    } else if (this.isolatedProductKey) {
      this.isolateProduct(tag, name, key);
    } else {
      // Sin aislamiento previo: aislar directamente
      this.isolateProduct(tag, name, key);
    }
  }

  isolateProduct(tag: string, name: string, key: string): void {
    this.hiddenInferenceProducts = new Set();
    // Ocultar todos los productos EXCEPTO el seleccionado
    const allProds = this.inferencesByTag.flatMap(g => g.products);
    allProds.forEach(p => {
      if (p.key !== key) {
        this.hiddenInferenceProducts.add(this.productKeyFor(p.tag, p.name));
      }
    });
    this.isolatedProductKey  = key;
    this.productSearchQuery  = '';
    this.cd.markForCheck();
  }

  clearProductIsolation(): void {
    this.hiddenInferenceProducts = new Set();
    this.isolatedProductKey      = null;
    this.productSearchQuery      = '';
    this.cd.markForCheck();
  }

  clearProductSearch(): void {
    this.productSearchQuery = '';
    this.cd.markForCheck();
  }

  /** Versión pública de productKey para uso interno de métodos públicos */
  private productKeyFor(tag: string, name: string): string {
    return `${String(tag).toLowerCase()}__${String(name)}`;
  }

  // ── Zoom & pan ───────────────────────────────────────────────────────────

  get imgTransformStyle(): string {
    if (this.imgZoom === 1 && this.imgTranslateX === 0 && this.imgTranslateY === 0) return 'none';
    return `translate(${this.imgTranslateX.toFixed(1)}px, ${this.imgTranslateY.toFixed(1)}px) scale(${this.imgZoom})`;
  }

  zoomIn(): void {
    const container = this.observedWrap;
    const cw = container?.clientWidth  ?? 400;
    const ch = container?.clientHeight ?? 300;
    const s2 = Math.min(5, +(this.imgZoom + 0.5).toFixed(1));
    if (s2 === this.imgZoom) return;
    this.imgTranslateX = this.imgTranslateX * s2 / this.imgZoom;
    this.imgTranslateY = this.imgTranslateY * s2 / this.imgZoom;
    this.imgZoom = s2;
    this.clampTranslate(cw, ch);
    this.cd.markForCheck();
  }

  zoomOut(): void {
    const container = this.observedWrap;
    const cw = container?.clientWidth  ?? 400;
    const ch = container?.clientHeight ?? 300;
    const s2 = Math.max(1, +(this.imgZoom - 0.5).toFixed(1));
    if (s2 === this.imgZoom) return;
    this.imgTranslateX = this.imgTranslateX * s2 / this.imgZoom;
    this.imgTranslateY = this.imgTranslateY * s2 / this.imgZoom;
    this.imgZoom = s2;
    if (s2 === 1) { this.imgTranslateX = 0; this.imgTranslateY = 0; }
    this.clampTranslate(cw, ch);
    this.cd.markForCheck();
  }

  resetZoom(): void {
    this.imgZoom = 1;
    this.imgTranslateX = 0;
    this.imgTranslateY = 0;
    this.cd.markForCheck();
  }

  onImgWheel(event: WheelEvent, container: HTMLElement): void {
    event.preventDefault();
    event.stopPropagation();
    const s1 = this.imgZoom;
    const delta = event.deltaY < 0 ? 0.25 : -0.25;
    const s2 = Math.min(5, Math.max(1, +(s1 + delta).toFixed(2)));
    if (s2 === s1) return;

    const rect = container.getBoundingClientRect();
    const mx = event.clientX - rect.left;
    const my = event.clientY - rect.top;
    const cw = container.clientWidth;
    const ch = container.clientHeight;

    // Pivota sobre la posición del cursor: el punto bajo el cursor queda fijo
    // Fórmula derivada de: screen(cx) = (cx - cw/2 + tx) * s + cw/2 + tx_screen
    this.imgTranslateX = mx - cw / 2 - (mx - cw / 2 - this.imgTranslateX) * s2 / s1;
    this.imgTranslateY = my - ch / 2 - (my - ch / 2 - this.imgTranslateY) * s2 / s1;
    this.imgZoom = s2;
    if (s2 === 1) { this.imgTranslateX = 0; this.imgTranslateY = 0; }
    this.clampTranslate(cw, ch);
    this.cd.markForCheck();
  }

  onImgPanStart(event: MouseEvent): void {
    if (this.imgZoom <= 1) return;
    event.preventDefault();
    this.isPanning = true;
    this.panStartX  = event.clientX;
    this.panStartY  = event.clientY;
    this.panStartTX = this.imgTranslateX;
    this.panStartTY = this.imgTranslateY;
  }

  onImgPanMove(event: MouseEvent): void {
    if (!this.isPanning) return;
    const container = this.observedWrap;
    const cw = container?.clientWidth  ?? 400;
    const ch = container?.clientHeight ?? 300;
    this.imgTranslateX = this.panStartTX + (event.clientX - this.panStartX);
    this.imgTranslateY = this.panStartTY + (event.clientY - this.panStartY);
    this.clampTranslate(cw, ch);
    this.cd.markForCheck();
  }

  onImgPanEnd(): void {
    this.isPanning = false;
  }

  // El contenido siempre debe cubrir el viewport — limita el desplazamiento
  private clampTranslate(cw: number, ch: number): void {
    if (this.imgZoom <= 1) { this.imgTranslateX = 0; this.imgTranslateY = 0; return; }
    const maxTX = cw * (this.imgZoom - 1) / 2;
    const maxTY = ch * (this.imgZoom - 1) / 2;
    this.imgTranslateX = Math.max(-maxTX, Math.min(maxTX, this.imgTranslateX));
    this.imgTranslateY = Math.max(-maxTY, Math.min(maxTY, this.imgTranslateY));
  }

  /**
   * Devuelve los bboxes ordenados por área DESCENDENTE.
   * Los grandes quedan al fondo (z-index bajo), los pequeños arriba (z-index alto)
   * → así el hover llega al bbox más pequeño visible bajo el cursor.
   */
  sortedBboxes(img: DetailGalleryImage): Array<{ obj: StoreDetailRecord; idx: number; zIndex: number }> {
    const objsWithArea = img.objects.map((obj, idx) => {
      const b = obj.bbox;
      const area = b ? (b.width * b.height) : 0;
      return { obj, idx, area };
    });
    // Orden: mayor área primero, menor área al final
    objsWithArea.sort((a, b) => b.area - a.area);
    // z-index: el primero (más grande) es bajo, el último (más pequeño) es alto
    return objsWithArea.map((it, i) => ({
      obj:    it.obj,
      idx:    it.idx,
      zIndex: i + 1,
    }));
  }

  toggleDetailExpanded(): void {
    this.detailExpanded = !this.detailExpanded;
    // Reflow del mapa si vuelve a verse
    if (!this.detailExpanded) {
      setTimeout(() => this.map?.invalidateSize({ animate: false }), 370);
    }
  }

  /** Toggle: si está oculto lo muestra, si está visible lo oculta */
  toggleInferenceTag(tag: string): void {
    const key = tag.toLowerCase();
    if (this.hiddenInferenceTags.has(key)) {
      this.hiddenInferenceTags.delete(key);
    } else {
      this.hiddenInferenceTags.add(key);
    }
    this.expandedInferenceKeys = new Set();
    this.cd.markForCheck();
  }

  /** @deprecated usar toggleInferenceTag */
  setInferenceTab(tag: string): void { this.toggleInferenceTag(tag); }

  /** Mostrar todos los tags — limpiar el Set de ocultos */
  clearInferenceTab(): void {
    this.hiddenInferenceTags.clear();
    this.expandedInferenceKeys = new Set();
    this.cd.markForCheck();
  }

  /** Total de detecciones sumando todos los tags */
  get allInferencesTotalCount(): number {
    return this.inferencesByTag.reduce((s, g) => s + g.totalCount, 0);
  }

  toggleInferenceExpand(key: string): void {
    if (this.expandedInferenceKeys.has(key)) {
      this.expandedInferenceKeys.delete(key);
    } else {
      this.expandedInferenceKeys.add(key);
    }
    this.cd.markForCheck();
  }

  /** @deprecated usar inferencesByTag */
  get filteredObjectSummary(): Array<{ key: string; name: string; tag: string; count: number; faces: number; empty: number; contaminant: number }> {
    return this.selectedImageObjectSummary;
  }

  get selectedImageTagCount(): number {
    const img = this.selectedDetailImage;
    if (!img) return 0;
    return new Set(img.objects.map(o => String(o.tag ?? 'otros'))).size;
  }

  get selectedImageFaces(): number {
    const img = this.selectedDetailImage;
    if (!img) return 0;
    return img.objects.reduce((acc, o) => acc + Number(o.faces ?? 0), 0);
  }

  get selectedImageEmpty(): number {
    const img = this.selectedDetailImage;
    if (!img) return 0;
    return img.objects.reduce((acc, o) => acc + Number(o.empty ?? 0), 0);
  }

  get selectedImageContaminant(): number {
    const img = this.selectedDetailImage;
    if (!img) return 0;
    return img.objects.reduce((acc, o) => acc + Number(o.contaminant ?? 0), 0);
  }

  private clearLayers(): void {
    if (this.heatLayer)   { this.map.removeLayer(this.heatLayer);   this.heatLayer   = null; }
    if (this.markerGroup) { this.map.removeLayer(this.markerGroup); this.markerGroup = null; }
  }

  private setBaseTileLayer(): void {
    if (!this.map) return;
    if (this.baseTileLayer) {
      this.map.removeLayer(this.baseTileLayer);
      this.baseTileLayer = null;
    }
    const url = 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
    this.baseTileLayer = L.tileLayer(url, {
      subdomains: 'abcd',
      maxZoom: 19,
    }).addTo(this.map);
  }

  private bindResizeObserver(): void {
    const el = this.mapEl?.nativeElement;
    if (!el || typeof ResizeObserver === 'undefined') return;
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => this.scheduleMapRefresh());
    this.resizeObserver.observe(el);
  }

  private scheduleMapRefresh(): void {
    if (!this.map) return;
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => {
      this.map?.invalidateSize(false);
      this.redraw({ fit: false });
    }, 90);
  }

  protected get filteredStores(): StorePoint[] {
    let base = this.cityStores;
    if (this.selectedLocality) base = base.filter(s => s.locality === this.selectedLocality);

    if (this.searchType === 'store' && this.searchQuery?.trim()) {
      const q = this.searchQuery.trim().toLowerCase();
      base = base.filter(s =>
        s.name.toLowerCase().includes(q) || s.locality.toLowerCase().includes(q)
      );
    } else if (this.searchType === 'product' && this.selectedProductId != null) {
      // Filtrar solo PDVs que tienen el producto seleccionado
      const pid = this.selectedProductId;
      base = base.filter(s => s.productIds?.includes(pid));
    }
    return base;
  }

  private fitMapToPoints(points: StorePoint[]): void {
    if (!this.map || !points.length) return;
    try {
      const bounds = L.latLngBounds(points.map(p => [p.lat, p.lng]));
      if (bounds.isValid()) {
        this.map.fitBounds(bounds, { padding: [30, 30], maxZoom: 14 });
      }
    } catch {
      // noop: si la capa aún no está lista no rompemos render
    }
  }

  private metricValue(s: StorePoint): number {
    return this.metric === 'visits'     ? s.visits / 150 :
           this.metric === 'score'      ? s.avgScore :
           this.metric === 'compliance' ? s.compliance :
           this.metric === 'empty'      ? Math.min(1, (s.emptyCount ?? 0) / 40) :
           this.metric === 'contaminant'? Math.min(1, (s.contaminantCount ?? 0) / 30) :
           Math.min(1, (s.faceCount ?? 0) / 60);
  }

  private drawHeat(points: StorePoint[]): void {
    const zoom = Number(this.map?.getZoom?.() ?? 11);
    const dynamicRadius = Math.max(12, Math.round(this.heatRadius * (0.7 + ((zoom - 10) * 0.12))));
    const data = points.map(s => [s.lat, s.lng, this.metricValue(s)]);
    this.heatLayer = (L as any).heatLayer(data, {
      radius:    dynamicRadius,
      blur:      20,
      maxZoom:   13,
      gradient:  this.heatGradient(),
    }).addTo(this.map);
  }

  private drawMarkers(points: StorePoint[]): void {
    this.markerGroup = L.layerGroup().addTo(this.map);
    for (const store of points) {
      this.drawSingleStoreMarker(store);
    }
  }

  private drawSingleStoreMarker(store: StorePoint): void {
      const val           = this.metricValue(store);
      const color         = this.localityColor(store.locality);
      const r             = 6 + val * 10;
      const isActive      = this.selectedStore?.name === store.name;
      const hasSelection  = !!this.selectedStore;
      const isDimmed      = hasSelection && !isActive;

      // Anillo pulsante exterior para el marcador activo
      if (isActive) {
        L.circleMarker([store.lat, store.lng], {
          radius:      r + 12,
          fillColor:   '#6366f1',
          color:       '#6366f1',
          weight:      0,
          fillOpacity: 0.15,
          interactive: false,
          className:   'shm-marker-pulse',
        }).addTo(this.markerGroup);
      }

      const htmlContent = `
        <div class="shm-custom-marker ${isActive ? 'is-active' : ''}" 
             style="background-color: ${isActive ? '#6366f1' : color}; opacity: ${isDimmed ? 0.35 : 1}; color: white; border: 2px solid white;">
          ${store.compliance < 0.5 || store.contaminantCount! > 0 ? '⚠' : (store.avgScore > 0.8 ? '★' : '')}
        </div>
      `;

      const customIcon = L.divIcon({
        className: 'leaflet-marker-icon leaflet-zoom-animated leaflet-interactive',
        html: htmlContent,
        iconSize: isActive ? [32, 32] : [24, 24],
        iconAnchor: isActive ? [16, 16] : [12, 12]
      });

      const circle = L.marker([store.lat, store.lng], { 
        icon: customIcon,
        zIndexOffset: isActive ? 1000 : 0 
      }).addTo(this.markerGroup);

      circle.on('mouseover', () => {
        const containerPt = this.map.latLngToContainerPoint([store.lat, store.lng]);
        // Detección de bordes: si el marcador está cerca del límite del mapa,
        // el tooltip se "flippea" hacia el lado contrario para no cortarse.
        const mapEl = this.mapEl?.nativeElement;
        const mapW  = mapEl?.clientWidth  ?? 9999;
        const mapH  = mapEl?.clientHeight ?? 9999;
        const TOOLTIP_W = 220;   // ancho aprox del tooltip
        const TOOLTIP_H = 68;    // alto aprox
        const MARGIN    = 20;

        const flipDown  = containerPt.y < TOOLTIP_H + MARGIN;                 // muy arriba → tooltip abajo
        const flipLeft  = containerPt.x > mapW - TOOLTIP_W / 2 - MARGIN;      // muy a la derecha → ancla izq
        const flipRight = containerPt.x < TOOLTIP_W / 2 + MARGIN;             // muy a la izq → ancla der

        this.zone.run(() => {
          this.tooltip = {
            store, x: containerPt.x, y: containerPt.y,
            flipDown, flipLeft, flipRight,
          };
          this.cd.markForCheck();
        });
      });
      circle.on('mouseout', () => {
        this.zone.run(() => { this.tooltip = null; this.cd.markForCheck(); });
      });
      circle.on('click', () => {
        this.zone.run(() => {
          this.selectStore(store);
          this.cd.markForCheck();
        });
      });
  }

  // ── Interacciones ─────────────────────────────────────────────────────────
  setMetric(m: HeatMetric): void {
    this.metric = m;
    this.computeStats();
    this.zone.runOutsideAngular(() => this.redraw({ fit: false }));
  }

  setHeatView(v: HeatView): void {
    this.heatView = v;
    this.metric = v === 'performance' ? 'visits' : 'empty';
    this.computeStats();
    this.zone.runOutsideAngular(() => this.redraw({ fit: false }));
  }

  setMode(m: 'heat' | 'markers'): void {
    this.viewMode = m;
    this.tooltip  = null;
    this.zone.runOutsideAngular(() => this.redraw({ fit: false }));
  }

  onRadiusChange(): void {
    if (this.viewMode !== 'heat') return;
    this.zone.runOutsideAngular(() => {
      if (this.heatLayer) this.heatLayer.setOptions({ radius: this.heatRadius });
    });
  }

  toggleLocality(name: string): void {
    this.selectedLocality = this.selectedLocality === name ? null : name;
    this.clearStoreSelection();
    this.zone.runOutsideAngular(() => this.redraw());
  }

  clearLocalityFilter(): void {
    if (!this.selectedLocality) return;
    this.selectedLocality = null;
    this.zone.runOutsideAngular(() => this.redraw());
  }

  clearStoreSelection(): void {
    this.selectedStore = null;
    this.storeDetails = [];
    this.detailImages = [];
    this.selectedDetailImageKey = null;
    this.detailByTag = [];
  }

  localityColor(name: string): string {
    return LOCALITY_COLORS[name] ?? '#94a3b8';
  }

  private sumByLocality(locality: string, key: 'emptyCount' | 'contaminantCount' | 'faceCount' | 'facesPtbn' | 'facesCcx'): number {
    return this.cityStores
      .filter(s => s.locality === locality)
      .reduce((acc, s) => acc + Number(s[key] ?? 0), 0);
  }

  private heatGradient(): Record<number, string> {
    if (this.heatView === 'findings') {
      return { 0.0: '#1f2937', 0.25: '#6366f1', 0.5: '#14b8a6', 0.75: '#f59e0b', 1.0: '#ef4444' };
    }
    return { 0.0: '#1e40af', 0.3: '#7c3aed', 0.6: '#db2777', 0.85: '#f59e0b', 1.0: '#fef08a' };
  }

  private selectStore(store: StorePoint): void {
    // Click en la misma tienda → no hacer nada
    if (this.selectedStore?.name === store.name) return;

    // Limpiar búsqueda al seleccionar — restaura todos los marcadores en el mapa
    this.searchQuery = '';
    this.showSearchDropdown = false;
    this.searchDropdownResults = [];

    const wasSelected = !!this.selectedStore;
    this.selectedStore = store;

    // Limpiar tooltip pegado: cuando el marcador se destruye en el redraw,
    // su mouseout no se dispara y queda fantasma. Lo borramos explícitamente.
    this.tooltip = null;

    // ⭐ Emitir al padre para que cargue el query 431 con filtro_pdv_name.
    // El padre actualizará config.detailsByStore[store.name] cuando regrese.
    // Mientras tanto, mostramos spinner local (evita flash de "Sin imágenes").
    this.internalLoading = true;
    this.storeSelected.emit({
      pdvName: store.name,
      pdvCode: (store as any).pdvCode ?? '',
    });

    const detailsMap = this.config?.detailsByStore ?? {};

    // 1. Match exacto por nombre de tienda (cache hit del 431 previo)
    const fromExact = detailsMap[store.name] ?? [];
    // 2. Match normalizado (variantes de capitalización)
    const fromNorm = fromExact.length ? [] : this.findDetailsByNormalizedStoreName(store.name, detailsMap);

    // En MODO REAL nunca usamos data de otras tiendas:
    //   - NO fallback a getFallbackQueryDetails (mostraba fotos de otra tienda)
    //   - NO fallback a pool '_all_'  (legacy del 432 sin store_name)
    // Si no hay match → vacío + spinner hasta que llegue el 431.
    this.storeDetails =
      fromExact.length ? fromExact :
      fromNorm.length  ? fromNorm  :
      this.simulated   ? this.buildMockStoreDetails(store) :
                         [];

    this.detailImages           = this.buildDetailGallery(this.storeDetails);
    this.selectedDetailImageKey = this.detailImages[0]?.key ?? null;
    this.detailByTag            = this.aggregateDetailsByTag(this.storeDetails);
    this.inferenceScoreMin      = 0;
    this.hiddenInferenceTags    = new Set();
    this.hiddenInferenceProducts= new Set();
    this.detailExpanded         = false;
    this.bboxViewMode           = 'boxes';
    this.storeDetailTab         = 'gallery';
    this.isolatedProductKey     = null;
    this.productSearchQuery     = '';
    this.resetZoom();

    // ⭐ Auto-expandir el producto del filtro activo en el panel de inferencias
    this.expandedInferenceKeys = this.buildInitialExpandedKeys();

    this.cd.markForCheck();

    // Guardar vista actual SOLO en la primera selección (no al cambiar de PDV)
    if (!wasSelected && this.map) {
      this.savedMapView = {
        center: this.map.getCenter(),
        zoom:   this.map.getZoom(),
      };
    }

    // Redibujar marcadores para destacar el seleccionado — SIN refittear el mapa
    // (queremos preservar la posición/zoom actual y solo cambiar marcadores).
    // El flyTo de abajo se encarga del posicionamiento final.
    this.redraw({ fit: false });

    if (wasSelected) {
      // Ya había un PDV seleccionado → el mapa ya está mini, vuela directo
      this.map?.flyTo([store.lat, store.lng], 15, {
        duration:      0.5,
        easeLinearity: 0.4,
      });
    } else {
      // Primera selección → esperar animación CSS de altura (350ms)
      setTimeout(() => {
        this.map?.invalidateSize({ animate: false });
        this.map?.flyTo([store.lat, store.lng], 15, {
          duration:      0.6,
          easeLinearity: 0.4,
        });
        // Scroll suave al panel de detalle
        const detail = this.host.nativeElement.querySelector('.shm-detail-panel');
        detail?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 370);
    }
  }

  closeDetail(): void {
    this.selectedStore = null;
    this.detailExpanded = false;
    this.internalLoading = false;
    // Limpiar resize observer
    this.wrapResizeObs?.disconnect();
    this.wrapResizeObs = undefined;
    this.observedWrap  = undefined;
    this.cd.markForCheck();
    // Redibujar sin refittear — el flyTo de abajo restaura la vista previa
    this.redraw({ fit: false });

    setTimeout(() => {
      this.map?.invalidateSize({ animate: false });
      // Restaurar vista previa o ajustar a todas las tiendas
      if (this.savedMapView) {
        this.map?.flyTo(this.savedMapView.center, this.savedMapView.zoom, {
          duration:      0.55,
          easeLinearity: 0.4,
        });
        this.savedMapView = null;
      } else if (this.filteredStores.length) {
        const bounds = L.latLngBounds(
          this.filteredStores.map(s => [s.lat, s.lng])
        );
        this.map?.fitBounds(bounds.pad(0.15), { animate: true, duration: 0.5 });
      }
    }, 370);
  }

  /** Scroll horizontal de la tira de miniaturas */
  scrollStrip(el: HTMLElement, dir: 'left' | 'right'): void {
    if (!el) return;
    const amount = el.clientWidth * 0.7;
    el.scrollBy({
      left: dir === 'left' ? -amount : amount,
      behavior: 'smooth',
    });
  }

  /** Formato compacto: 2400 → 2.4k, 1500 → 1.5k */
  formatCompact(n: number): string {
    if (n >= 1000) return (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'k';
    return String(n);
  }

  /** ESC cierra el detalle */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.selectedStore) this.closeDetail();
  }

  /** Flechas ⬅️ ➡️ navegan entre imágenes del strip */
  @HostListener('document:keydown.arrowleft')
  onArrowLeft(): void {
    if (!this.selectedStore || !this.detailImages.length) return;
    const idx = this.detailImages.findIndex(i => i.key === this.selectedDetailImageKey);
    const prev = idx <= 0 ? this.detailImages.length - 1 : idx - 1;
    this.selectDetailImage(this.detailImages[prev].key);
  }

  @HostListener('document:keydown.arrowright')
  onArrowRight(): void {
    if (!this.selectedStore || !this.detailImages.length) return;
    const idx = this.detailImages.findIndex(i => i.key === this.selectedDetailImageKey);
    const next = idx >= this.detailImages.length - 1 ? 0 : idx + 1;
    this.selectDetailImage(this.detailImages[next].key);
  }

  private buildMockStoreDetails(store: StorePoint): StoreDetailRecord[] {
    // Estructura que imita la respuesta de /api/card/432/query/json
    // Cada "imagen" = un registro de reconocimiento con N objetos_detectados
    const PRODUCTS: { name: string; tag: string }[] = [
      { name: 'Gaseosa Cola 1.5L',     tag: 'postobon'     },
      { name: 'Gaseosa Uva 1L',        tag: 'postobon'     },
      { name: 'Agua Cristal 600ml',    tag: 'postobon'     },
      { name: 'Hit Caja Mango',        tag: 'postobon'     },
      { name: 'Mr. Tea Limón',         tag: 'postobon'     },
      { name: 'Pepsi 1.5L',            tag: 'competencia'  },
      { name: 'Coca-Cola 2L',          tag: 'competencia'  },
      { name: 'Agua Brisa 1L',         tag: 'competencia'  },
      { name: 'Red Bull 250ml',        tag: 'competencia'  },
      { name: 'Genérico sin etiqueta', tag: 'otros'        },
    ];

    const rng = (s: number) => { const x = Math.sin(s) * 10000; return x - Math.floor(x); };
    const base = Math.abs(this.hashCode(`${store.name}-${store.locality}`));
    const IMG_COUNT = 10; // 10 ejecuciones simuladas
    const records: StoreDetailRecord[] = [];

    for (let imgIdx = 0; imgIdx < IMG_COUNT; imgIdx++) {
      const s0  = base + imgIdx * 31;
      const date = new Date(2026, 4, 11 - (imgIdx % 7),
        8 + Math.floor(rng(s0) * 10), Math.floor(rng(s0 + 1) * 60));
      const timestamp = date.toISOString().replace('T', ' ').slice(0, 16);
      const imageUrl  = `https://picsum.photos/seed/pdv-${base}-${imgIdx}/820/540`;
      const objCount  = 4 + Math.floor(rng(s0 + 2) * 7); // 4-10 objetos por imagen

      for (let objIdx = 0; objIdx < objCount; objIdx++) {
        const s1  = s0 + objIdx * 7;
        const prod = PRODUCTS[Math.floor(rng(s1) * PRODUCTS.length)];
        const score = 0.55 + rng(s1 + 1) * 0.44;

        // Bounding box normalizado en píxeles (imagen 820×540)
        const W = 820; const H = 540;
        const bW = 60 + Math.floor(rng(s1 + 2) * 120);
        const bH = 80 + Math.floor(rng(s1 + 3) * 160);
        const bX = Math.floor(rng(s1 + 4) * (W - bW));
        const bY = Math.floor(rng(s1 + 5) * (H - bH));

        records.push({
          imageUrl,
          timestamp,
          tag:          prod.tag,
          objectName:   prod.name,
          score,
          faces:        rng(s1 + 6) > 0.4 ? 1 : 0,
          empty:        rng(s1 + 7) < 0.15 ? 1 : 0,
          contaminant:  rng(s1 + 8) < 0.08 ? 1 : 0,
          bbox: { x_min: bX, y_min: bY, x_max: bX + bW, y_max: bY + bH, width: bW, height: bH },
          storeName: store.name,
        });
      }
    }
    return records;
  }

  private aggregateDetailsByTag(records: StoreDetailRecord[]): { tag: string; faces: number; empty: number; contaminant: number }[] {
    const map: Record<string, { faces: number; empty: number; contaminant: number }> = {};
    records.forEach(r => {
      if (!map[r.tag]) {
        map[r.tag] = { faces: 0, empty: 0, contaminant: 0 };
      }
      map[r.tag].faces += r.faces;
      map[r.tag].empty += r.empty;
      map[r.tag].contaminant += r.contaminant;
    });
    return Object.entries(map)
      .map(([tag, v]) => ({ tag, ...v }))
      .sort((a, b) => b.faces - a.faces);
  }

  private hashCode(value: string): number {
    let h = 0;
    for (let i = 0; i < value.length; i++) {
      h = (h << 5) - h + value.charCodeAt(i);
      h |= 0;
    }
    return h;
  }

  /**
   * Construye el Set inicial de claves expandidas para el panel de inferencias.
   * Si hay un producto activo en el filtro del mapa, lo expande automáticamente
   * para que el usuario lo encuentre sin tener que buscar manualmente.
   */
  private buildInitialExpandedKeys(): Set<string> {
    const keys = new Set<string>();
    if (!this.selectedProductId || !this.config?.catalog) return keys;

    const catalogEntry = this.config.catalog.get(this.selectedProductId);
    if (!catalogEntry) return keys;

    const nameLower = catalogEntry.name.toLowerCase();

    // Recorrer inferencesByTag y encontrar la clave del producto que coincide
    for (const group of this.inferencesByTag) {
      for (const prod of group.products) {
        if (prod.name.toLowerCase() === nameLower) {
          keys.add(prod.key);
          return keys;   // solo uno puede coincidir
        }
      }
    }
    return keys;
  }

  private findDetailsByNormalizedStoreName(
    storeName: string,
    detailsMap: Record<string, StoreDetailRecord[]>,
  ): StoreDetailRecord[] {
    const target = this.normalizeStoreName(storeName);
    for (const [name, records] of Object.entries(detailsMap)) {
      if (this.normalizeStoreName(name) === target && Array.isArray(records) && records.length) {
        return records;
      }
    }
    return [];
  }

  private normalizeStoreName(value: string): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  onDetailImgLoad(event: Event, img: DetailGalleryImage, wrapEl?: HTMLElement): void {
    const el = event.target as HTMLImageElement;
    img.imgW = el.naturalWidth  || 1;
    img.imgH = el.naturalHeight || 1;
    // Dimensiones reales del contenedor para calcular letterbox
    const container = wrapEl ?? (el.parentElement as HTMLElement);
    img.containerW = container?.clientWidth  || img.imgW;
    img.containerH = container?.clientHeight || img.imgH;

    // ResizeObserver: actualiza dims al expandir/colapsar el panel
    // o cualquier cambio de layout para que los bboxes escalen con la imagen
    if (container && this.observedWrap !== container) {
      this.wrapResizeObs?.disconnect();
      this.observedWrap = container;
      this.wrapResizeObs = new ResizeObserver(() => {
        this.zone.run(() => {
          const focus = this.selectedDetailImage;
          if (focus) {
            focus.containerW = container.clientWidth;
            focus.containerH = container.clientHeight;
            this.cd.markForCheck();
          }
        });
      });
      this.wrapResizeObs.observe(container);
    }

    this.cd.markForCheck();
  }

  /**
   * Calcula posición y tamaño del bbox en px compensando el letterbox
   * que produce object-fit:contain.  Devuelve posición absoluta en px
   * referenciada al contenedor .shm-dp-viewer__img-wrap.
   */
  detailBboxStyle(obj: StoreDetailRecord, img: DetailGalleryImage): Record<string, string> {
    if (!obj.bbox || !img.imgW || !img.imgH || !img.containerW || !img.containerH) {
      return { display: 'none' };
    }
    const { imgW, imgH, containerW, containerH } = img;
    const imgRatio = imgW / imgH;
    const boxRatio = containerW / containerH;

    let renderedW: number, renderedH: number, offsetX: number, offsetY: number;
    if (imgRatio > boxRatio) {
      // imagen más ancha → ajusta por ancho, letterbox arriba/abajo
      renderedW = containerW;
      renderedH = containerW / imgRatio;
      offsetX   = 0;
      offsetY   = (containerH - renderedH) / 2;
    } else {
      // imagen más alta → ajusta por alto, pillarbox izq/der
      renderedH = containerH;
      renderedW = containerH * imgRatio;
      offsetY   = 0;
      offsetX   = (containerW - renderedW) / 2;
    }

    const left   = offsetX + (obj.bbox.x_min  / imgW) * renderedW;
    const top    = offsetY + (obj.bbox.y_min  / imgH) * renderedH;
    const width  =           (obj.bbox.width  / imgW) * renderedW;
    const height =           (obj.bbox.height / imgH) * renderedH;

    return {
      left:   `${left.toFixed(1)}px`,
      top:    `${top.toFixed(1)}px`,
      width:  `${Math.max(4, width).toFixed(1)}px`,
      height: `${Math.max(4, height).toFixed(1)}px`,
    };
  }

  private buildDetailGallery(records: StoreDetailRecord[]): DetailGalleryImage[] {
    const byImage: Record<string, DetailGalleryImage> = {};
    records.forEach((rec, idx) => {
      if (!rec.imageUrl) return;
      const key = `${rec.imageUrl}__${rec.timestamp || idx}`;
      if (!byImage[key]) {
        byImage[key] = {
          key,
          imageUrl:   rec.imageUrl,
          timestamp:  rec.timestamp,
          imgW:       0,
          imgH:       0,
          containerW: 0,
          containerH: 0,
          objects:    [],
        };
      }
      byImage[key].objects.push(rec);
    });
    return Object.values(byImage).sort((a, b) => String(b.timestamp).localeCompare(String(a.timestamp)));
  }

  private getFallbackQueryDetails(
    storeName: string,
    detailsMap: Record<string, StoreDetailRecord[]>,
  ): StoreDetailRecord[] {
    const entries = Object.entries(detailsMap).filter(([, list]) => Array.isArray(list) && list.length > 0);
    if (!entries.length) return [];
    const idx = Math.abs(this.hashCode(storeName)) % entries.length;
    return entries[idx][1];
  }

  selectDetailImage(key: string): void {
    this.selectedDetailImageKey = key;
    // Resetear estado del panel de inferencias al cambiar de imagen
    this.hiddenInferenceTags    = new Set();
    this.hiddenInferenceProducts= new Set();
    this.expandedInferenceKeys  = new Set();
    this.hoveredBboxIdx        = null;
    this.isolatedProductKey    = null;
    this.productSearchQuery    = '';
    this.resetZoom();
    // ⭐ Forzar CD para que los chips (productos/vacíos/compet) se actualicen
    this.cd.markForCheck();
  }

  formatTimelineLabel(raw: string): string {
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return String(raw ?? '');
    return new Intl.DateTimeFormat('es-CO', {
      month: '2-digit', day: '2-digit',
      hour: '2-digit',  minute: '2-digit', hour12: false,
    }).format(date);
  }

  // ── Helpers del dialog ────────────────────────────────────────────────────
  tagColor(tag: string): string {
    const map: Record<string, string> = {
      'postobon':    '#6366f1',
      'competencia': '#f43f5e',
      'nevera':      '#22d3ee',
      'precios':     '#f59e0b',
      'otros':       '#64748b',
    };
    return map[tag?.toLowerCase()] ?? '#94a3b8';
  }

  complianceColor(v: number): string {
    return v >= 0.8 ? '#22c55e' : v >= 0.6 ? '#f59e0b' : '#ef4444';
  }

  getImageTags(img: DetailGalleryImage): string[] {
    return [...new Set(img.objects.map(o => String(o.tag ?? 'otros')))];
  }

  sumDetailByKey(key: 'faces' | 'empty' | 'contaminant'): number {
    return this.storeDetails.reduce((acc, r) => acc + Number(r[key] ?? 0), 0);
  }

  tagPct(faces: number): number {
    const total = this.detailByTag.reduce((s, r) => s + r.faces, 0) || 1;
    return (faces / total) * 100;
  }
}
